import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  Experiment,
  ExperimentClient,
  ExperimentUser,
  Variants,
} from '@amplitude/experiment-react-native-client';
import fastDeepEqual from 'fast-deep-equal';

import { EventTypes, logEvent } from 'state/amplitude';
import { appVersion } from 'utils/app-version-info';
import { LogLevel } from 'utils/datadog/types';
import { getDeviceIdAsync } from 'utils/device-id';
import { getApiKey, minDevLogLevel } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import logger from 'utils/logger';
import noop from 'utils/noop';

import { RunningExperiments } from './running-experiments';
import { IExperimentsContext, VariantOptions } from './types';

export { RunningExperiments } from './running-experiments';

const Context = React.createContext<IExperimentsContext>({
  isLoading: false,
  getVariantValue: (_flag: string | RunningExperiments) => undefined,
  logExposure: (_flag: RunningExperiments) => void 0,
  handleLogin: noop,
  handleLogout: noop,
  allVariants: {} as Variants,
  variant: (_flag: RunningExperiments, _expectedVariant: string, _options?: VariantOptions) =>
    false,
});

export const useExperiments = () => useContext(Context);

export const experiments = Experiment.initializeWithAmplitudeAnalytics(
  getApiKey({ key: 'amplitudeExperiments' }),
  {
    debug: __DEV__ && minDevLogLevel() <= LogLevel.debug,
    automaticExposureTracking: false, // we track this manually for many experiments
    automaticFetchOnAmplitudeIdentityChange: true,
  }
);

export const experimentsFetchPromise = fetchExperimentsForUser(
  syncStorage.getString(SyncStorageKey.USER_ID)
);

async function fetchExperimentsForUser(userId: string): Promise<ExperimentClient> {
  const deviceId = await getDeviceIdAsync();
  const user: ExperimentUser = {
    device_id: deviceId.toLowerCase(),
    version: appVersion,
  };

  if (userId) {
    user.user_id = userId;
  }

  return experiments.fetch(user);
}

export const ExperimentsProvider = ({ children }: { children: React.ReactNode }) => {
  const flagExposuresHandled = useRef(new Map<string, boolean>());
  const [isLoading, setIsLoading] = useState(true);
  const [variants, setVariants] = useState(experiments.all()); // from localstorage initially or maybe get lucky and from server if very fast

  const handleFetchPromise = useCallback((fetchPromise: Promise<ExperimentClient>) => {
    fetchPromise
      .then(result => {
        const newVariants = result.all();
        setVariants(prevVariants => {
          const equal = fastDeepEqual(newVariants, prevVariants);
          return equal ? prevVariants : newVariants;
        });
      })
      .catch(error => logger.error({ error, message: 'Amplitude experiments init failure' }))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    handleFetchPromise(experimentsFetchPromise);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Override the typical getVariant function to utilize our local cache approach.
  const value = useMemo(() => {
    const logExposure = (
      flagKey: string | RunningExperiments,
      type: 'Manual' | 'Auto' = 'Manual'
    ) => {
      const foundVariant = variants[flagKey];
      const foundValue = foundVariant?.value;
      const flagAndValueKey = flagKey + foundVariant?.value;

      if (!flagExposuresHandled.current.has(flagAndValueKey) && foundValue) {
        logEvent('$exposure', EventTypes.Other, {
          flag_key: flagKey,
          variant: foundValue,
        });
        flagExposuresHandled.current.set(flagAndValueKey, true);
        logger.info(
          `[Experiment] ${type} Logging exposure for "${flagKey}", variant value: "${foundVariant.value}"`
        );
      }
    };

    return {
      isLoading,
      getVariantValue: (flagKey: string | RunningExperiments) => variants[flagKey],
      logExposure,
      handleLogin: (userId: string) => {
        handleFetchPromise(fetchExperimentsForUser(userId));
      },
      handleLogout: () => {
        experiments.clear();
        flagExposuresHandled.current.clear();
        handleFetchPromise(fetchExperimentsForUser(null));
      },
      allVariants: variants,
      variant: (
        flagKey: string,
        expectedVariant: string,
        options = { skipExposureLogging: false }
      ) => {
        if (options.skipExposureLogging !== true) {
          logExposure(flagKey, 'Auto');
        }

        const foundVariant = variants[flagKey];
        return foundVariant?.value === expectedVariant;
      },
    };
  }, [isLoading, variants, handleFetchPromise]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
